import { Company } from '@/api/model/company'
import { Pipeline } from '@/api/model/pipeline'
import { PipelinePhase } from '@/api/model/pipeline-phase'

export enum BranchType {
    DEFAULT = 'default',
    SMART_MARKETING = 'smart-marketing',
}

export enum UserType {
    DEFAULT = 'default',
    SMART_MARKETING = 'smart-marketing',
}

export enum Gender {
    MALE = 'male',
    FEMALE = 'female',
    NEUTRAL = 'neutral',
    UNKNOWN = 'unknown',
}
export const availableGenders = (): Gender[] => [
    Gender.MALE,
    Gender.FEMALE,
    Gender.NEUTRAL,
    Gender.UNKNOWN,
]

export enum Role {
    EMPLOYEE = 'employee',
    TEAM_LEAD = 'team-lead',
    BRANCH_MANAGER = 'branch-manager',
    COMPANY_MANAGER = 'company-manager',
}
export const availableRoles = (): Role[] => [
    Role.EMPLOYEE,
    Role.TEAM_LEAD,
    Role.BRANCH_MANAGER,
    Role.COMPANY_MANAGER,
]

export type ReferralView =
    | 'user'
    | 'user_in'
    | 'user_out'
    | 'branches'
    | 'branches_in'
    | 'branches_out'

export const availableReferralViews = (): ReferralView[] => [
    'user',
    'user_in',
    'user_out',
    'branches',
    'branches_in',
    'branches_out',
]

export type ReferralFilterDirection =
    | 'in'
    | 'out'

export type ReferralAge =
    | 'age90'
    | 'age180'
    | 'age365'

export enum CredentialsType {
    ACCELERATE = 'accelerate',
    ACTIVE_CAMPAIGN = 'active_campaign',
    MAILCHIMP = 'mailchimp',
    REALWORKS = 'realworks',
    TYPEFORM = 'typeform',
}
export const availableCredentialsTypes = (): CredentialsType[] => [
    CredentialsType.ACCELERATE,
    CredentialsType.ACTIVE_CAMPAIGN,
    CredentialsType.MAILCHIMP,
    CredentialsType.REALWORKS,
    CredentialsType.TYPEFORM,
]

export enum CompanyType {
    REALTOR = 'realtor',
    MORTGAGE = 'mortgage',
}

export const availableCompanyTypes = (): CompanyType[] => [
    CompanyType.REALTOR,
    CompanyType.MORTGAGE,
]

export enum CompanyTargetType {
    TEAM = 'team',
    USER = 'user',
}
export const availableCompanyTargetTypes = (): CompanyTargetType[] => [
    CompanyTargetType.TEAM,
    CompanyTargetType.USER,
]

export enum CompanyLicenseType {
    BRANCH_LICENSE = 'branch-license',
    MAQR_ONLY = 'maqr-only',
    BASIC = 'basic',
    BASIC_PLUS = 'basic-plus',
    PREMIUM = 'premium',
    PREMIUM_PLUS = 'premium-plus',
}
export const availableLicenseTypes = (): CompanyLicenseType[] => [
    CompanyLicenseType.BRANCH_LICENSE,
    CompanyLicenseType.MAQR_ONLY,
    CompanyLicenseType.BASIC,
    CompanyLicenseType.BASIC_PLUS,
    CompanyLicenseType.PREMIUM,
    CompanyLicenseType.PREMIUM_PLUS,
]

export type Module =
    | 'customer-portal'
    | 'orders'
    | 'doc-flow'
    | 'email-integration'
    | 'virtual-colleague'
    | 'gamification'

// no enum as there is no validation in keyvue-api
export const availableModules = (): Module[] => [
    'customer-portal',
    'orders',
    'doc-flow',
    'email-integration',
    'virtual-colleague',
    'gamification',
]

export enum TargetDirection {
    IN = 'in',
    OUT = 'out',
}
export const availableTargetDirections = (): TargetDirection[] => [
    TargetDirection.IN,
    TargetDirection.OUT,
]

export enum ReferralType {
    APPRAISAL = 'appraisal',
    EPA = 'epa',
    INFORMATION = 'information',
    MORTGAGE = 'mortgage',
    PURCHASE = 'purchase',
    PHOTO = 'photo',
    SALE = 'sale',
    SIGN_PLACEMENT = 'sign-placement',
    TAXATION = 'taxation',
    TECHNICAL_APPRAISAL = 'technical-appraisal',
    NEW_BUILD = 'new-build',
}
export const availableReferralTypes = (): ReferralType[] => [
    ReferralType.APPRAISAL,
    ReferralType.EPA,
    ReferralType.INFORMATION,
    ReferralType.MORTGAGE,
    ReferralType.PURCHASE,
    ReferralType.PHOTO,
    ReferralType.SALE,
    ReferralType.SIGN_PLACEMENT,
    ReferralType.TAXATION,
    ReferralType.TECHNICAL_APPRAISAL,
    ReferralType.NEW_BUILD,
]

export enum ReferralStatus {
    NEW = 'new',
    APPOINTMENT = 'appointment',
    PROPOSAL = 'proposal',
    ON_HOLD = 'on-hold',
    PENDING = 'pending',
    UNREACHABLE = 'unreachable',
    OPEN = 'open',
    SUCCESSFUL = 'successful',
    PROCESSED = 'processed',
    CLOSED = 'closed',
}
export const availableReferralStatuses = (company: Company|null = null): ReferralStatus[] => {
    if (company && company.pipelinesEnabled) {
        return [
            ReferralStatus.OPEN,
            ReferralStatus.SUCCESSFUL,
            ReferralStatus.CLOSED,
        ]
    }

    return [
        ReferralStatus.NEW,
        ReferralStatus.APPOINTMENT,
        ReferralStatus.PROPOSAL,
        ReferralStatus.ON_HOLD,
        ReferralStatus.PENDING,
        ReferralStatus.UNREACHABLE,
        ReferralStatus.SUCCESSFUL,
        ReferralStatus.PROCESSED,
        ReferralStatus.CLOSED,
    ]
}
export const allowedReferralStatuses = (type: ReferralType, status: ReferralStatus): ReferralStatus[] => {
    let statuses = {}

    switch (type) {
        default:
            statuses = {
                'new': ['new', 'appointment', 'pending', 'unreachable', 'successful', 'closed'],
                'appointment': ['appointment', 'proposal', 'on-hold', 'successful', 'closed'],
                'proposal': ['proposal', 'on-hold', 'successful', 'closed'],
                'on-hold': ['appointment', 'proposal', 'on-hold', 'successful', 'closed'],
                'pending': ['pending', 'appointment', 'proposal', 'successful', 'closed'],
                'unreachable': ['appointment', 'pending', 'unreachable', 'successful', 'closed'],
                'successful': ['successful', 'closed'],
                'processed': ['processed', 'successful', 'closed'],
                'closed': ['closed', 'successful'],
            }
            break
    }

    return statuses[status]
}

export enum ReferralStatusReason {
    WAITING_INTERNAL = 'waiting-internal',
    WAITING_EXTERNAL = 'waiting-external',
    WAITING_CONTACT = 'waiting-contact',
    INFORMATION = 'information',
    NEGATIVE_ADVICE = 'negative-advice',
    NO_SUITABLE_TIME = 'no-suitable-time',
    NO_TIME = 'no-time',
    NOT_INTERESTED = 'not-interested',
    OTHER = 'other',
    THINKING = 'thinking',
    TO_COMPETITOR = 'to-competitor',
    VOICEMAIL = 'voicemail',
    WITHDRAWN = 'withdrawn',
    WRONG_CONTACT = 'wrong-contact',
    WRONG_CONTACT_DETAILS = 'wrong-contact-details',
    REOPENED = 'reopened',
    UNREACHABLE = 'unreachable',
    LONG_TERM = 'long-term',
    CONTACTED_PER_EMAIL = 'contacted-per-email',
    WAITING_NEW_BUILD = 'waiting-new-build',
    PROCESSED_REOPENED = 'processed-reopened',
    PROCESSED_CLOSED = 'processed-closed',
    AUTO_CLOSED = 'auto-closed',
}

export const availableReferralStatusReasons = (): ReferralStatusReason[] => [
    ReferralStatusReason.WAITING_INTERNAL,
    ReferralStatusReason.WAITING_EXTERNAL,
    ReferralStatusReason.WAITING_CONTACT,
    ReferralStatusReason.INFORMATION,
    ReferralStatusReason.NEGATIVE_ADVICE,
    ReferralStatusReason.NO_SUITABLE_TIME,
    ReferralStatusReason.NO_TIME,
    ReferralStatusReason.NOT_INTERESTED,
    ReferralStatusReason.OTHER,
    ReferralStatusReason.THINKING,
    ReferralStatusReason.TO_COMPETITOR,
    ReferralStatusReason.VOICEMAIL,
    ReferralStatusReason.WITHDRAWN,
    ReferralStatusReason.WRONG_CONTACT,
    ReferralStatusReason.WRONG_CONTACT_DETAILS,
    ReferralStatusReason.REOPENED,
    ReferralStatusReason.UNREACHABLE,
    ReferralStatusReason.LONG_TERM,
    ReferralStatusReason.CONTACTED_PER_EMAIL,
    ReferralStatusReason.WAITING_NEW_BUILD,
    ReferralStatusReason.PROCESSED_REOPENED,
    ReferralStatusReason.PROCESSED_CLOSED,
    ReferralStatusReason.AUTO_CLOSED,
]
export const allowedReferralStatusReasons = (type: ReferralType, status: ReferralStatus): ReferralStatusReason[] => {
    let statusReasons = {}

    switch (type) {
        default:
            statusReasons = {
                'new': [],
                'appointment': [],
                'proposal': [],
                'on-hold': ['waiting-internal', 'waiting-external', 'waiting-contact', 'long-term', 'waiting-new-build'],
                'pending': ['information', 'thinking', 'no-suitable-time', 'no-time', 'wrong-contact', 'other'],
                'unreachable': ['voicemail', 'wrong-contact-details', 'contacted-per-email'],
                'successful': [],
                'processed': [],
                'closed': ['not-interested', 'negative-advice', 'withdrawn', 'to-competitor', 'unreachable', 'other'],
            }
            break
    }

    return statusReasons[status]
}

export enum OrderType {
    PURCHASE = 'purchase',
    SALE = 'sale',
    MORTGAGE = 'mortgage',
    RENTAL = 'rental',
}

export const orderTypes = (): OrderType[] => {
    return [
        OrderType.PURCHASE,
        OrderType.SALE,
        OrderType.MORTGAGE,
        OrderType.RENTAL,
    ]
}

export enum OrderStatus {
    PENDING = 'pending',
    SUCCESSFUL = 'successful',
    UNSUCCESSFUL = 'unsuccessful',
}

export const orderStatusses = (): OrderStatus[] => [
    OrderStatus.PENDING,
    OrderStatus.SUCCESSFUL,
    OrderStatus.UNSUCCESSFUL,
]

export enum ReferralNodeType {
    APPRAISAL = 'appraisal',
    EPA = 'epa',
    INFORMATION = 'information',
    MORTGAGE = 'mortgage',
    PURCHASE = 'purchase',
    PHOTO = 'photo',
    SALE = 'sale',
    SIGN_PLACEMENT = 'sign-placement',
    TAXATION = 'taxation',
    TECHNICAL_APPRAISAL = 'technical-appraisal',
    NEW_BUILD = 'new-build',
}
export const availableReferralNodeTypes = (): ReferralNodeType[] => [
    ReferralNodeType.APPRAISAL,
    ReferralNodeType.EPA,
    ReferralNodeType.INFORMATION,
    ReferralNodeType.MORTGAGE,
    ReferralNodeType.PURCHASE,
    ReferralNodeType.PHOTO,
    ReferralNodeType.SALE,
    ReferralNodeType.SIGN_PLACEMENT,
    ReferralNodeType.TAXATION,
    ReferralNodeType.TECHNICAL_APPRAISAL,
    ReferralNodeType.NEW_BUILD,
]

export enum TriggerType {
    change = 'change',
    appointment = 'appointment',
}

export const availableTriggerTypes = (): TriggerType[] => [
    TriggerType.change,
    TriggerType.appointment,
]

export enum DelayUnit {
    minute = 'minute',
    hour = 'hour',
    day = 'day',
}

export const availableDelayUnits = (): DelayUnit[] => [
    DelayUnit.day,
    DelayUnit.hour,
    DelayUnit.minute,
]

export enum ReferralOrigin {
    all = 'all',
    internal = 'internal',
    external = 'external',
}

export const availableReferralOrigins = (): ReferralOrigin[] => [
    ReferralOrigin.external,
    ReferralOrigin.internal,
    ReferralOrigin.all,
]

export enum ActionType {
    MEETING = 'meeting',
    EMAIL = 'email',
    CALL = 'call',
    REMINDER = 'reminder',
    LETTER = 'letter',
    BILLING = 'billing',
    UPDATE = 'update',
    NOTIFICATION = 'notification',
    ADD_TO_PIPELINE = 'add-to-pipeline',
}

export const availableActionTypes = (): ActionType[] => [
    ActionType.BILLING,
    ActionType.CALL,
    ActionType.EMAIL,
    ActionType.LETTER,
    ActionType.MEETING,
    ActionType.REMINDER,
    ActionType.UPDATE,
    ActionType.NOTIFICATION,
    ActionType.ADD_TO_PIPELINE,
]

export enum ActionAssignToType {
    referredBy = 'referred-by',
    assignedTo = 'assigned-to',
    actionAssignedTo = 'action-assigned-to',
}

export const availableActionAssignToTypes = (): ActionAssignToType[] => [
    ActionAssignToType.assignedTo,
    ActionAssignToType.referredBy,
    ActionAssignToType.actionAssignedTo,
]

export enum ActionAppointmentType {
    VIDEO = 'video',
    PHONE = 'phone',
    OFFICE = 'office',
    CUSTOMER = 'customer',
}

export const availableActionAppointmentTypes = (): ActionAppointmentType[] => [
    ActionAppointmentType.CUSTOMER,
    ActionAppointmentType.OFFICE,
    ActionAppointmentType.PHONE,
    ActionAppointmentType.VIDEO,
]

export interface ReferralFilters {
    priority?: number[] // really only true/false, but a number because booleans cause issues
    type?: ReferralType[]
    status?: ReferralStatus[]
    statusReason?: ReferralStatusReason[]

    // List of model id's, not resource identifiers
    contact?: string[]
    referredBy?: string[]
    referredByBranch?: string[]
    assignedTo?: string[]
    assignedToBranch?: string[]
}

export enum ContactView {
    CONTACT = 'contact',
    ADDRESS = 'address',
}

export const availableContactViews = (): ContactView[] => {
    return [
        ContactView.CONTACT,
        ContactView.ADDRESS,
    ]
}

export enum CommentOrigin {
    SYSTEM = 'system',
    USER = 'user',
    VIRTUAL_COLLEAGUE = 'virtual-colleague',
}

export enum CommentVisibility {
    PERSONAL = 'personal',
    PRIVATE = 'private',
    PUBLIC = 'public',
}

export const availableCommentVisibilities = (): CommentVisibility[] => {
    return [
        CommentVisibility.PERSONAL,
        CommentVisibility.PRIVATE,
        CommentVisibility.PUBLIC,
    ]
}

export enum DashboardView {
    USER = 'user',
    BRANCHES = 'branches',
}

export const availableDashboardViews = (): DashboardView[] => {
    return [
        DashboardView.USER,
        DashboardView.BRANCHES,
    ]
}

export enum ScheduledDashboardExportSchedule {
    WEEKLY = 'weekly',
    MONTHLY = 'monthly',
}

export const availableScheduledDashboardExportSchedules = (): ScheduledDashboardExportSchedule[] => {
    return [
        ScheduledDashboardExportSchedule.WEEKLY,
        ScheduledDashboardExportSchedule.MONTHLY,
    ]
}

export enum NewBuildType {
    EXISTING = 'existing',
    NEW = 'new',
}

export const availableNewBuildTypes = (): NewBuildType[] => {
    return [
        NewBuildType.EXISTING,
        NewBuildType.NEW,
    ]
}

export interface PageRequest {
    pagination: boolean,
    page: number,
    itemsPerPage: number,
}

export interface PageResult<T> {
    collection: T[],
    totalItems: number,
    totalPages: number,
}

export interface ReferralCount {
    statusAll: number,
    statusNew: number,
    statusAppointment: number,
    statusProposal: number,
    statusOnHold: number,
    statusPending: number,
    statusUnreachable: number,
    statusSuccessful: number,
    statusProcessed: number,
    statusClosed: number,
    scorePositive: number,
    scoreNeutral: number,
    scoreNegative: number,
    typeAppraisal: number,
    typeEpa: number,
    typeInformation: number,
    typeMortgage: number,
    typePurchase: number,
    typePhoto: number,
    typeSale: number,
    typeSignPlacement: number,
    typeTaxation: number,
    typeTechnicalAppraisal: number,
    typeNewBuild: number,
    age90: number,
    age180: number,
    age365: number,
}

export interface NewReferralCounts {
    age90: number,
    age180: number,
    age365: number,
    ageOther: number,
    scorePositive: number,
    scoreNeutral: number,
    scoreNegative: number,
    countsForAllPipelines: number,
    countsPerPipeline: Array<{ count: number, pipeline: string }>
    countsPerPhase: Array<{ count: number, phase: string }>
    countsPerCategory: Array<{ count: number, category: string }>
    countsPerStatus: Array<{ count: number, status: string }>
}

export interface AcceptedReferralTypes {
    [type: string]: boolean
}

export interface AssignedZipcodes {
    [type: string]: string[]
}

export interface SmartMarketingSelection {
    company: Company|null,
    includeInternalContacts: boolean,
    includeExternalContacts: boolean,
    includeRealworksRelaties: boolean,
    syncedRealworksKenmerken: string[],
    excludedRealworksKenmerken: string[],
    syncedRealworksRelatietypes: string[],
    syncedRealworksStatus: boolean|null,
    updatedFrom: Date,
    updatedUntil: Date,
}

export enum ProductType {
    TYPE_ONCE = 'once',
    TYPE_MONTHLY = 'monthly',
    TYPE_YEARLY = 'yearly',
}

export const availableProductTypes = (): ProductType[] => {
    return [
        ProductType.TYPE_ONCE,
        ProductType.TYPE_MONTHLY,
        ProductType.TYPE_YEARLY,
    ]
}

export enum WidgetType {
    AVATAR = 'avatar',
    POPUP = 'popup',
}

export enum WidgetFlowType {
    AGENT = 'agent',
    INFORMATION = 'information',
    VALUATION = 'valuation',
    CHECKLIST = 'checklist',
}

export enum WidgetGoalType {
    CALL = 'call',
    EMAIL = 'email',
    CUSTOM = 'custom',
    VALUATION = 'valuation',
    CHECKLIST = 'checklist',
    INFORMATION = 'information',
    AGENT = 'agent',
}

export interface WidgetGoal {
    type: WidgetGoalType,
    custom: string,
    label: string,
}

export enum WidgetPopupType {
    RECTANGLE = 'rectangle',
    CIRCULAR = 'circular',
    CARD = 'card',
}

export enum WidgetPopupPosition {
    LEFT = 'left',
    RIGHT = 'right',
}

export enum WidgetPopupAnimation {
    NONE = 'none',
    OPEN = 'OPEN',
}

export enum WidgetFont {
    ROBOTO = 'Roboto',
    OPENSANS = 'Open-sans',
    MONTSERRAT = 'Montserrat',
}

export interface WidgetPopupContent {
    flow: WidgetFlowType
    button: string
    line1: string
    line2: string
    line3: string
    line4: string
}

export enum WidgetAvatarStyle {
    A,
    B,
    C,
}

export interface WidgetInformationTopic {
    topic: string,
    tag: string
}

export enum BadgeType {
    MOST_CREATED_LEADS = 'most-created-leads',
    HIGHEST_REVENUE = 'highest-revenue',
    HIGHEST_LEAD_QUALITY = 'highest-lead-quality',
    FASTEST_CONVERSION = 'fastest-conversion',
    MOST_CONVERSION = 'most-conversion',
    MOST_APPOINTMENT = 'most-appointment',
    MOST_PROPOSALS = 'most-proposals',
    FASTEST_LEAD_PICKUP = 'fastest-lead-pickup',
}

export const BadgeTypes = [
    BadgeType.MOST_CREATED_LEADS,
    BadgeType.HIGHEST_REVENUE,
    BadgeType.HIGHEST_LEAD_QUALITY,
    BadgeType.FASTEST_CONVERSION,
    BadgeType.MOST_CONVERSION,
    BadgeType.MOST_APPOINTMENT,
    BadgeType.MOST_PROPOSALS,
    BadgeType.FASTEST_LEAD_PICKUP,
]

export enum BadgeColor {
    GREEN = 'keyvue-green',
    BLUE = 'keyvue-blue',
    PINK = 'keyvue-pink',
    GOLD = 'gold',
    SILVER = 'silver',
    BRONZE = 'bronze',
}

export const BadgeColorCodes = {
    [BadgeColor.GOLD]:  '#ffc552',
    [BadgeColor.SILVER]:  '#A8A8AA',
    [BadgeColor.BRONZE]:  '#ce853e',
}

export interface RealworksAgendaStatus {
    systemid: number,
    status: string,
    actief: boolean,
    volgorde: number,
}

export interface RealworksTaakStatus {
    systemid: number,
    status: string,
    actief: boolean,
    volgorde: number,
}

export enum PipelineCategory {
    PURCHASE = 'purchase',
    SALE = 'sale',
    MORTGAGE = 'mortgage',
    TAXATION = 'taxation',
    SUSTAINABILITY = 'sustainability',
    INSURANCE = 'insurance',
    NEW_BUILD = 'new-build',
    RENTAL = 'rental',
    BUSINESS_REAL_ESTATE = 'business-real-estate',
}

export const PipelineCategories = [
    PipelineCategory.PURCHASE,
    PipelineCategory.SALE,
    PipelineCategory.MORTGAGE,
    PipelineCategory.TAXATION,
    PipelineCategory.SUSTAINABILITY,
    PipelineCategory.INSURANCE,
    PipelineCategory.RENTAL,
    PipelineCategory.NEW_BUILD,
    PipelineCategory.BUSINESS_REAL_ESTATE,
]

export enum PipelinePhaseCategory {
    ENTRY = 'entry',
    APPROACH = 'approach',
    DECISION = 'decision',
}

export const PipelinePhaseCategories = [
    PipelinePhaseCategory.ENTRY,
    PipelinePhaseCategory.APPROACH,
    PipelinePhaseCategory.DECISION,
]

export enum ReferralChannel {
    USER = 'user',
    ONLINE = 'online',
    REALWORKS = 'realworks',
}

export const availableReferralChannels = (): ReferralChannel[] => {
    return [
        ReferralChannel.USER,
        ReferralChannel.ONLINE,
        ReferralChannel.REALWORKS,
    ]
}

export interface EmmaDefaults {
    [key: string]: EmmaDefaultsEntry
}

export interface EmmaDefaultsEntry {
    pipeline: string|null,
    phase: string|null,
    defaultStatus: ReferralStatus,
}

export interface Permission {
    name: string,
    roles: Role[]
}
